import { useEffect, useMemo, useState } from 'react';
import { ListChecks, Plus, X } from 'lucide-react';
import { loadTasks } from '../components/TaskPicker.jsx';

/**
 * Tâche partagée de l'affaire (#76) — équivalent tactile du `TaskPicker`
 * desktop : on tape, la liste se filtre, on touche pour choisir ; si rien ne
 * correspond exactement, la première ligne propose de CRÉER la tâche.
 *
 * Contrat : `value` / `onChange` = `{ taskId, taskName, label }`.
 *  - `taskId`   : tâche existante choisie ;
 *  - `taskName` : libellé à créer côté serveur (`resolveTaskId`, à
 *    l'enregistrement de l'imputation ou de l'allocation) ;
 *  - ni l'un ni l'autre : aucune tâche (le ✕ retire la tâche de la saisie,
 *    il n'archive PAS la tâche partagée, qui reste à l'équipe).
 * `taskPayload(value)` traduit en champs d'API.
 *
 * Lecture `GET /api/affaire-tasks` bornée à `mineOnly` en app native. Son
 * indisponibilité n'empêche pas la saisie (liste vide, création possible).
 */

export const NO_TASK = { taskId: null, taskName: null, label: null };

/** Valeur initiale depuis une saisie existante (`{ task: { id, name } }`). */
export function taskValueOf(entity) {
  return entity?.task?.id
    ? { taskId: entity.task.id, taskName: null, label: entity.task.name }
    : NO_TASK;
}

/** Champs d'API : `{ taskId }`, `{ taskId: null, taskName }` ou détachement. */
export function taskPayload(value) {
  if (value?.taskId) return { taskId: value.taskId };
  if (value?.taskName) return { taskId: null, taskName: value.taskName };
  return { taskId: null, taskName: null };
}

/** Vrai si `value` diffère de la tâche portée par `entity`. */
export function taskChanged(value, entity) {
  const current = entity?.task?.id ?? null;
  if (value?.taskName) return true;
  return (value?.taskId ?? null) !== current;
}

const norm = (s) =>
  String(s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();

export default function MobileTaskField({ affaireId, value, onChange, disabled }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    setQuery('');
    setSearching(false);
    if (!affaireId) {
      setTasks([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    loadTasks(affaireId)
      .then((items) => {
        if (!cancelled) setTasks(items);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [affaireId]);

  const trimmed = query.trim();
  const matches = useMemo(() => {
    const q = norm(trimmed);
    return q ? tasks.filter((t) => norm(t.name).includes(q)) : tasks;
  }, [tasks, trimmed]);
  const exact = tasks.some((t) => norm(t.name) === norm(trimmed));

  if (!affaireId) return null;

  const pick = (next) => {
    onChange(next);
    setQuery('');
    setSearching(false);
  };

  const hasValue = Boolean(value?.taskId || value?.taskName);

  return (
    <div className="m-field">
      <span className="m-field__label">Tâche</span>
      {hasValue && !searching ? (
        <div className="m-pick__selected">
          <ListChecks size={16} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
          <span className="m-pick__label">
            {value.label || value.taskName}
            {value.taskName ? <span className="m-pick__sub">Nouvelle tâche</span> : null}
          </span>
          <button
            type="button"
            className="m-btn"
            disabled={disabled}
            onClick={() => setSearching(true)}
          >
            Changer
          </button>
          <button
            type="button"
            className="m-iconbtn m-iconbtn--danger"
            disabled={disabled}
            onClick={() => pick(NO_TASK)}
            title="Retirer la tâche"
            aria-label="Retirer la tâche"
          >
            <X size={16} />
          </button>
        </div>
      ) : !searching ? (
        <button
          type="button"
          className="m-btn"
          style={{ alignSelf: 'flex-start' }}
          disabled={disabled}
          onClick={() => setSearching(true)}
        >
          <Plus size={15} /> Ajouter une tâche
        </button>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="m-input"
              type="search"
              value={query}
              autoFocus
              autoComplete="off"
              enterKeyHint="done"
              maxLength={255}
              placeholder="Rechercher ou créer une tâche"
              aria-label="Rechercher ou créer une tâche"
              style={{ flex: 1 }}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button type="button" className="m-btn" onClick={() => setSearching(false)}>
              Annuler
            </button>
          </div>
          <div className="m-pick" role="listbox" aria-label="Tâches">
            {trimmed && !exact ? (
              <button
                type="button"
                role="option"
                aria-selected="false"
                className="m-pick__item"
                onClick={() => pick({ taskId: null, taskName: trimmed, label: trimmed })}
              >
                <Plus size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                <span className="m-pick__label">
                  <span className="m-pick__name">Créer « {trimmed} »</span>
                  <span className="m-pick__sub">Partagée avec l’équipe de l’affaire</span>
                </span>
              </button>
            ) : null}
            {loading ? (
              <div className="m-pick__empty">Chargement…</div>
            ) : matches.length === 0 ? (
              trimmed ? null : (
                <div className="m-pick__empty">Aucune tâche : tapez un nom pour en créer une.</div>
              )
            ) : (
              matches.map((t) => (
                <button
                  type="button"
                  role="option"
                  aria-selected={value?.taskId === t.id}
                  key={t.id}
                  className="m-pick__item"
                  onClick={() => pick({ taskId: t.id, taskName: null, label: t.name })}
                >
                  <ListChecks size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span className="m-pick__label">
                    <span className="m-pick__name">{t.name}</span>
                  </span>
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
